import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    effect,
    EventEmitter,
    inject,
    input,
    Input,
    OnInit,
    Output,
    viewChild,
    ViewChild,
} from "@angular/core";
import { SearchCriteria } from "../../pojo/SearchCriteria";
import { AngBridgeUtil } from "../../util/ang.bridge.util";
import { ListColumn } from "../../standalone/lists/list.table.types";
import { SearchListTableHelper } from "../../standalone/lists/search.list.table.helper";
import { S25OptModule } from "../s25-opt/s25.opt.module";
import { S25TableModule } from "../s25-table/s25.table.module";
import type { Table } from "../s25-table/Table";
import { Bind } from "../../decorators/bind.decorator";
import { ListService } from "../../services/list.service";
import { OptService } from "../../services/opt.service";
import { OptColumnChooser, ViewButtonValue, S25OptComponent } from "../s25-opt/s25.opt.component";
import { S25Util } from "../../util/s25-util";
import type { Proto } from "../../pojo/Proto";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { PreferenceService } from "../../services/preference.service";
import { type HasItemType, Item } from "../../pojo/Item";
import { S25Const } from "../../util/s25-const";
import { ContactService } from "../../services/contact.service";
import type { Task } from "../../pojo/Task";
import { S25TaskEditComponent } from "../s25-task/s25.task.edit.component";
import { S25TaskStatusComponent } from "../s25-task/s25.task.status.component";
import { TaskTiersService } from "../../services/task/task.tiers.service";
import type { Nullable, Refreshable } from "../../pojo/Util";
import type { Search } from "./search";
import { LoginStore } from "../../stores/login.store";
import { AriaLive } from "../../services/aria.live.service";
type ISODateString = Proto.ISODateString;

const stickyColumnPreference = {
    [Item.Ids.Event]: "25L_event_search_columns",
    [Item.Ids.Location]: "25L_space_search_columns",
    [Item.Ids.Resource]: "25L_resource_search_columns",
    [Item.Ids.Organization]: "25L_org_search_columns",
    [Item.Ids.Task]: "25L_task_overview_columns",
    [Item.Ids.Contact]: "25L_cont_search_columns",
} as const satisfies Record<Search.TypeId, string>;

@Component({
    selector: "s25-ng-search-results-list",
    template: `
        @if (optInit && isInit) {
            <s25-ng-opt
                [modelBean]="optBean"
                [hasListTab]="true"
                [hasCalendarTab]="itemTypeId !== 3"
                [hasAvailDailyTab]="itemTypeId === 1 || itemTypeId === 4 || itemTypeId === 6"
                [hasAvailWeeklyTab]="itemTypeId === 4"
                [selectedTab]="'list'"
                [hasDatepicker]="itemTypeId === 1"
                [hasVariableDatepicker]="true"
                [hasDaysSelector]="true"
                [hasDateViews]="itemTypeId === 1"
                [selectedDateView]="dateView"
                [columnChooser]="columnChooser"
                [hasCreateTodo]="itemTypeId === 10"
                [hasRefresh]="true"
                [help]="'default'"
                [hasBulkEdit]="true"
                (tabChange)="onTabChange($event)"
                (dateChange)="onDateChange($event)"
                (endDateChange)="onEndDateChange($event)"
                (dateViewChange)="onDateViewChange($event)"
                (refreshed)="refresh()"
                (columnChooserChange)="onColumnChooserChange($event)"
            ></s25-ng-opt>
        }
        @if (isInit) {
            <s25-ng-table
                [columnSortable]="true"
                [dataSource]="tableData"
                [stickyColumnSortPreference]="S25Const.itemId2Name[itemTypeId]"
                [unlimitedWidth]="true"
                [pivotThreshold]="640"
                [hasTotalRowCount]="true"
                [minColWidth]="50"
            ></s25-ng-table>
        }
    `,
    styles: `
        ::ng-deep s25-ng-search-results-list tbody tr td input[type="checkbox"] {
            height: 16px !important;
        }

        ::ng-deep s25-ng-search-results-list s25-item {
            color: rgb(37, 115, 167);
        }

        ::ng-deep s25-ng-search-results-list s25-item s25-popover {
            display: block !important;
        }

        ::ng-deep s25-ng-search-results-list .s25-ng-table--cell.stateTentative {
            font-style: italic;
        }

        ::ng-deep s25-ng-search-results-list .s25-ng-table--cell.stateCancelled {
            color: #d62000;
            font-weight: 400;
        }

        ::ng-deep .nm-party--on s25-ng-search-results-list .s25-ng-table--cell.stateCancelled {
            color: #ff837a;
        }

        ::ng-deep s25-ng-search-results-list .s25-ng-table--cell.stateRequested_changes {
            color: #f90;
            font-weight: 400;
        }

        ::ng-deep .nm-party--on s25-ng-search-results-list .s25-ng-table--cell.stateRequested_changes {
            color: #ffb366;
        }
        ::ng-deep s25-ng-search-results-list s25-ng-table .italic {
            font-style: italic;
        }

        ::ng-deep s25-ng-search-results-list s25-ng-task-edit > span {
            display: block !important;
        }

        ::ng-deep s25-ng-search-results-list s25-ng-table s25-ng-editable-date .editable-input {
            width: 100% !important;
        }

        ::ng-deep s25-ng-search-results-list s25-ng-table .tableWrapper {
            border: 1px solid #e5e5e5;
        }

        ::ng-deep s25-ng-search-results-list s25-ng-table tbody tr:not(:last-of-type) {
            border-bottom: 1px solid #e5e5e5;
        }

        ::ng-deep s25-ng-search-results-list s25-ng-table .s25-ng-table--header:not(:first-child),
        ::ng-deep s25-ng-search-results-list s25-ng-table .s25-ng-table--cell:not(:first-child) {
            border-left: 1px solid #e5e5e5;
        }

        ::ng-deep .nm-party--on s25-ng-search-results-list s25-ng-table .tableWrapper {
            border: 1px solid rgba(255, 255, 255, 0.24);
        }

        ::ng-deep .nm-party--on s25-ng-search-results-list s25-ng-table tbody tr:not(:last-of-type) {
            border-bottom: 1px solid rgba(255, 255, 255, 0.24);
        }

        ::ng-deep .nm-party--on s25-ng-search-results-list s25-ng-table .s25-ng-table--header:not(:first-child),
        ::ng-deep .nm-party--on s25-ng-search-results-list s25-ng-table .s25-ng-table--cell:not(:first-child) {
            border-left: 1px solid rgba(255, 255, 255, 0.24);
        }

        ::ng-deep .s25-object-org,
        ::ng-deep .s25-object-contact {
            cursor: pointer !important;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [S25OptModule, S25TableModule],
})
export class S25SearchResultsListComponent implements OnInit, Refreshable, HasItemType<Search.TypeId> {
    @Input() itemTypeId: Search.TypeId;
    readonly query = input<string>();
    readonly searchModel = input<SearchCriteria.Model>();
    readonly queryGenerator = input<Nullable<() => Promise<{ query: string; postSearch?: () => void }>>>();
    @Output() tabChange = new EventEmitter<"availabilityWeekly" | "list" | "availability" | "calendar">();

    readonly tableComponent = viewChild(S25TableComponent);
    @ViewChild(S25OptComponent) optComponent: S25OptComponent;

    private readonly helper = new SearchListTableHelper(this.tableComponent, {
        getWorkflowChained: () => this.workflowChained,
        getUserId: () => this.userId,
        onTaskAction: (state, context) => this.onTaskAction(state, context),
    });

    isInit = false;
    optInit = false;
    optBean: any = { hasOpt: true }; // tried use "OptBean" type get error, use "any" instead;
    tableData: Table.Paginated = {
        type: "paginated",
        dataSource: this.getRows,
        columns: [],
        defaultPageSize: 25,
        pageSizes: [25, 50, 100],
        customRowCount: this.getRowCount,
    };
    columnChooser: OptColumnChooser;
    startDate: ISODateString;
    endDate: ISODateString;
    dateView: Nullable<ViewButtonValue>;
    paginationCacheId: Nullable<number>;
    S25Const = S25Const;
    data: any;
    userId: number;
    workflowChained: boolean;
    sortColumn: string;
    sortOrder?: "asc" | "desc" | null;

    readonly loginStore = inject(LoginStore);
    readonly changeDetector = inject(ChangeDetectorRef);

    constructor() {
        AngBridgeUtil.init(this);
        effect(() => {
            this.optBean.searchQuery = this.query();
            this.changeDetector.detectChanges();
        });
        effect(() => {
            this.optBean.searchModel = this.searchModel();
            this.changeDetector.detectChanges();
        });
    }

    async ngOnInit() {
        const [optData, prefs, userId, workflowPref] = await Promise.all([
            OptService.getS25Opt("list", S25Const.itemId2Name[this.itemTypeId]),
            PreferenceService.getGenericPreference("list", [
                `sticky_sort_col_${S25Const.itemId2Name[this.itemTypeId]}`,
                `sticky_sort_col_dir_${S25Const.itemId2Name[this.itemTypeId]}`,
                `sticky_page_size_${S25Const.itemId2Name[this.itemTypeId]}`,
            ]),
            ContactService.getCurrentId(),
            TaskTiersService.isWorkflowChained(),
        ]);
        this.dateView = optData.pref.dates_option || "recentHistory";
        this.tableData.defaultPageSize = prefs[`sticky_page_size_${S25Const.itemId2Name[this.itemTypeId]}`];
        this.userId = userId;
        this.workflowChained = workflowPref;
        this.optBean.objectId = this.itemTypeId;
        this.isInit = true;
        this.changeDetector.detectChanges();
    }

    @Bind
    async getRows(query: Table.PaginatedQuery): Promise<Table.DataSourceResponse> {
        const data = (await this.getData(query)) || [];
        this.optBean.totalSearchRowCount = data?.rows?.[0]?.count || data?.rows?.length;
        this.optBean.searchCacheId = data?.cacheId;

        // Get data first then init opt bar
        if (this.optInit) {
            this.optComponent.ngOnInit();
        } else {
            this.optInit = true;
            this.changeDetector.detectChanges();
        }

        const totalRows = data.rows?.[0]?.count ?? data.rows?.length ?? 0;
        this.announceResults(totalRows);

        return {
            rows: data.rows.map((row: any) => this.mapToRow(data.cols, row)),
            totalRows,
        };
    }

    getResultCount(): number | undefined {
        return this.data?.rows?.[0]?.count ?? this.data?.rows?.length ?? undefined;
    }

    private announceResults(count: number): void {
        const typeName = S25Const.itemId2Name[this.itemTypeId];
        const plural = count === 1 ? typeName : typeName + "s";
        if (count === 0) {
            AriaLive.announce(`No ${typeName}s found`);
        } else {
            AriaLive.announce(`Found ${count} ${plural}`);
        }
    }

    async getData(tableQuery: Table.PaginatedQuery) {
        if (tableQuery.forceRefresh) this.paginationCacheId = undefined; // If refresh is forced, don't reuse cache
        if (tableQuery.sortColumn.id !== this.sortColumn || tableQuery.sortColumn.order !== this.sortOrder) {
            // If sorting changed, reset pagination
            this.paginationCacheId = undefined;
            this.tableComponent()!.currentPage = 0;
            tableQuery.page = 0;
        }
        this.sortColumn = tableQuery.sortColumn.id;
        this.sortOrder = tableQuery.sortColumn.order;

        const { query, postSearch } = (await this.queryGenerator()?.()) ?? { query: this.query()! };

        const taskCount = this.data?.taskCount;
        this.data = await ListService.getListData({
            typeId: this.itemTypeId,
            query,
            page: tableQuery.page + 1,
            pageSize: tableQuery.pageSize,
            sortColumn: tableQuery.sortColumn.id,
            sortOrder: tableQuery.sortColumn.order,
            cacheId: this.paginationCacheId,
            dateOption: this.dateView,
            startDate: !this.dateView ? this.startDate : undefined,
            endDate: !this.dateView ? this.endDate : undefined,
        });
        if (this.itemTypeId === Item.Ids.Task && !this.data.taskCount) {
            this.data.taskCount = taskCount;
        }
        this.paginationCacheId = this.data.cacheId;
        postSearch?.();

        // Update columns in table
        const columns = this.data.cols.map((column: ListColumn) =>
            this.helper.buildTableColumn(column, { includeInstructorMinWidth: true }),
        );
        this.tableComponent()!.setColumns(columns);

        // Update columns in column chooser
        this.columnChooser = {
            prefName: stickyColumnPreference[this.itemTypeId],
            columns: this.data.cols.map((column: ListColumn) => {
                const columnId = this.helper.getColumnId(column);
                if (columnId === "isFav") {
                    return { id: columnId, name: "Favorite", checked: true, permanent: true };
                }
                return { id: columnId, name: column.name, checked: !!column.isVisible };
            }),
        };
        this.onColumnChooserChange(this.columnChooser);
        this.changeDetector.detectChanges();
        return this.data;
    }

    @Bind
    mapToRow(columns: any[], row: any): Table.Row {
        return this.helper.mapToRow(columns, row, {
            getId: (_, listRow: any) => listRow.contextId,
            getName: (_, listRow: any) => {
                const nameIndex = this.itemTypeId === Item.Ids.Task || !this.loginStore.loggedIn() ? 0 : 1;
                return listRow.row[nameIndex]?.itemName ?? String(listRow.contextId);
            },
        });
    }

    onTabChange(tab: "availabilityWeekly" | "list" | "availability" | "calendar") {
        this.tabChange.emit(tab);
    }

    focusViewButton(tab: "availabilityWeekly" | "list" | "availability" | "calendar"): boolean {
        return this.optComponent?.focusViewButton(tab) ?? false;
    }

    onDateChange(date: Date) {
        if (!date) return;
        this.startDate = S25Util.date.toS25ISODateTimeStr(S25Util.date.toStartOfDay(date))!;
        this.dateView = undefined;
    }

    onEndDateChange(date: Date) {
        if (!date) return;
        if (this.endDate) this.dateView = undefined; // Do not update view on first change
        this.endDate = S25Util.date.toS25ISODateTimeStr(S25Util.date.toEndOfDay(date))!;
        this.tableComponent()!.currentPage = 0; // Reset pagination on date change
    }

    refresh() {
        return this.tableComponent()?.refresh(true);
    }

    onDateViewChange(view: ViewButtonValue) {
        this.dateView = view;
        this.tableComponent()!.currentPage = 0; // Reset pagination on view change
    }

    onColumnChooserChange(columnChooser: OptColumnChooser) {
        const visible = new Set(columnChooser.columns.filter((column) => column.checked).map((column) => column.id));
        this.tableComponent()!.setVisibleColumns(visible);
    }

    @Bind
    onTaskAction(state: Task.State, context: Table.CellCallbackContext) {
        // Update task item column
        const taskItem = context.row.cells.task_item?.instance as S25TaskEditComponent;
        if (taskItem) {
            taskItem.taskModel.itemStateId = state;
            taskItem.refresh();
        }
        // Update status column
        const status = context.row.cells.status?.instance as S25TaskStatusComponent;
        if (status) {
            status.task.task_state = state;
            status.refresh();
        }
    }

    @Bind
    getRowCount(counts: Table.RowCounts) {
        const type = S25Util.firstCharToUpper(S25Const.itemId2Name[this.itemTypeId]) + (counts.total === 1 ? "" : "s");
        if (this.itemTypeId === Item.Ids.Task && this.data.taskCount !== counts.total) {
            const taskItems = `Task Item${counts.total === 1 ? "" : "s"}`;
            return `${counts.total} Matching ${taskItems} with ${this.data.taskCount} Tasks`;
        }
        return `${counts.total} Matching ${type}`;
    }
}
